import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { Box, IconButton, Typography } from '@mui/material';
import { grey, lightBlue } from '@mui/material/colors';
import { has } from 'lodash/object';
import moment from 'moment-timezone';

import ReimbursementWorklistApi from '../../../../api/patient/ReimbursementWorklistApi';
import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import { dateTime12, DD_SLASH_MMM_SLASH_YYYY } from '../../../../constants/dateFormat';
import { toBill } from '../../../../services/financial';
import useFilterSyncCache from '../../../eui/EuiFiltersContext/hooks/useFilterSyncCache';
import { generateUrlByKey } from '../../../root/router';
import {
  RBS_APPROVED,
  RBS_PENDING,
  reimbursementStatusesMap
} from '../../patient-source/Patients/PatientInfo/EncountersSection/PaymentsSection/reimbursementConstants';
import { statusChangeNotification } from '../../patient-source/Patients/PatientInfo/ReimbursementRequest/ReimbursementRequest';
import EditNoteModal from '../shared/worklist-table-note/EditNoteModal/EditNoteModal';
import ViewNoteModal from '../shared/worklist-table-note/ViewNoteModal/ViewNoteModal';

import AssigneeChangeDropdown from './AssigneeChangeDropdown/AssigneeChangeDropdown';
import StatusChangeDropdown from './StatusChangeDropdown/StatusChangeDropdown';

export default function useDataGrid() {
  const [gridData, setGridData] = useState([]);

  const columns = useMemo(
    () => [
      {
        field: 'patientId',
        headerName: 'Patient ID',
        flex: 1,
        renderCell({ value, row }) {
          return (
            <Link
              to={generateUrlByKey('Patients.Patient Profile.Patient Studies', {
                patientId: row.patientUniqueIdentifier,
                ssuPatientId: row.ssuPatientId
              })}
            >
              {value}
            </Link>
          );
        }
      },
      {
        field: 'mrn',
        headerName: 'MRN',
        flex: 1
      },
      {
        field: 'eventNumber',
        headerName: 'EventID',
        flex: 1
      },
      {
        field: 'submissionTime',
        headerName: 'Submission date',
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(dateTime12) : '';
        },
        type: 'date',
        align: 'center',
        minWidth: 180
      },
      {
        field: 'submittedBy',
        headerName: 'Submitted By',
        flex: 1
      },
      {
        field: 'approvedAt',
        headerName: 'Approved At',
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '';
        },
        type: 'date',
        align: 'center'
      },
      {
        field: 'approvedBy',
        headerName: 'Approved By',
        flex: 1
      },
      {
        field: 'processingTime',
        headerName: 'Processing Time',
        flex: 1
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1
      },
      {
        field: 'sponsor',
        headerName: 'Sponsor',
        flex: 1
      },
      {
        field: 'pcn',
        headerName: 'PCN',
        flex: 1
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1
      },
      {
        field: 'siteNumber',
        headerName: 'Site Number',
        flex: 1
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1
      },
      {
        field: 'encounterDate',
        headerName: 'Encounter date',
        flex: 1,
        valueGetter(value) {
          return value ? moment(value).toDate() : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '';
        },
        type: 'date',
        align: 'center'
      },
      {
        field: 'subjectId',
        headerName: 'SubjectID',
        flex: 1
      },
      {
        field: 'reimbursementType',
        headerName: 'Reimbursement Type',
        flex: 1
      },
      {
        field: 'totalAmount',
        headerName: 'Amount',
        flex: 1,
        type: 'number',
        maxWidth: 100,
        valueGetter(value) {
          return toBill(value);
        },
        renderCell({ value, row }) {
          return (
            <Link
              to={generateUrlByKey('Reimbursements.Reimbursement request', {
                reimbursementId: row.id,
                ssuPatientId: row.ssuPatientId,
                patientId: row.patientUniqueIdentifier
              })}
            >
              ${value}
            </Link>
          );
        }
      },
      {
        field: 'numberOfItems',
        headerName: '# of items',
        flex: 1,
        type: 'number',
        maxWidth: 100,
        align: 'center'
      },
      {
        field: 'assigneePersonnelName',
        headerName: 'Assigned',
        flex: 1,
        minWidth: 200,
        renderCell({ row }) {
          if (row?.status !== RBS_APPROVED) {
            return (
              <AssigneeChangeDropdown
                row={row}
                onChangeSuccess={function(data) {
                  setGridData(function(state) {
                    return state.map(function(r) {
                      if (r.id !== row.id) return r;
                      return {
                        ...r,
                        ...data
                      };
                    });
                  });
                }}
              />
            );
          }
          return <Typography variant="body1">{row?.assigneePersonnelName || ''}</Typography>;
        }
      },
      {
        field: 'comment',
        headerName: 'Comment',
        flex: 1
      },
      {
        field: 'note',
        headerName: 'Note',
        flex: 1,
        maxWidth: 100,
        renderCell({ value, row }) {
          return (
            <IconButton
              onClick={function() {
                ModalBoxes.open({
                  component:
                    row.status !== RBS_APPROVED ? (
                      <EditNoteModal
                        row={row}
                        onSave={function(note, markAsRead) {
                          return ReimbursementWorklistApi.updateWorklistRow(row.id, {
                            note,
                            markAsRead
                          });
                        }}
                      />
                    ) : (
                      <ViewNoteModal row={row} />
                    ),
                  onClose(data) {
                    if (has(data, 'note') && has(data, 'isNoteRead')) {
                      setGridData(function(state) {
                        return state.map(function(r) {
                          if (r.id !== row.id) return r;
                          return {
                            ...r,
                            ...data
                          };
                        });
                      });
                    }
                  }
                });
              }}
            >
              {value && !row?.isNoteRead ? (
                <MarkEmailUnreadIcon data-testid="MarkEmailUnreadIcon" htmlColor={lightBlue[500]} />
              ) : (
                <EmailIcon data-testid="EmailIcon" htmlColor={grey[500]} />
              )}
            </IconButton>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Action',
        flex: 1,
        minWidth: 200,
        valueGetter(value) {
          return reimbursementStatusesMap[value] || '';
        },
        renderCell({ value, row }) {
          if (row.status === RBS_PENDING) {
            return (
              <StatusChangeDropdown
                row={row}
                onChangeSuccess={function(data) {
                  NotificationManager.success(statusChangeNotification(data?.status));
                  setGridData(function(state) {
                    return state.map(function(r) {
                      if (r.id !== row.id) return r;
                      return { ...r, ...data };
                    });
                  });
                }}
              />
            );
          }
          return (
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Typography variant="body1">{value}</Typography>
            </Box>
          );
        }
      }
    ],
    []
  );

  useFilterSyncCache({ uniqKey: 'dataGridState' });

  return [columns, gridData, setGridData];
}
